<template>
  <div class="flex flex-col border-b border-gray-100 last:border-0">
    <v-list-item
      class="pl-0 h-[60px]"
      :class="{ 'opacity-50 grayscale pointer-events-none-children': isMarkedForRemoval }"
      color="primary20"
      :title="subject.subject_name"
      :subtitle="subject.subject_description"
      @click="emit('toggle')"
    >
      <template v-slot:prepend>
        <v-avatar color="primary30">
          <span class="text-md font-bold">{{ getInitials(subject.subject_name) }}</span>
        </v-avatar>
      </template>

      <template v-slot:append>
        <div v-if="subject.effective_relation" class="mr-3">
          <v-chip size="small" color="primary" variant="tonal" class="font-weight-medium">
            <span
              v-if="getRoleTag(subject.effective_relation)"
              class="text-[11px] px-1.5 py-0.5 rounded mr-1.5"
              :style="{
                border: `1px solid ${getRoleTag(subject.effective_relation)!.borderColor}`,
                background: getRoleTag(subject.effective_relation)!.bgColor,
                color: getRoleTag(subject.effective_relation)!.textColor
              }"
            >
              {{ getRoleTag(subject.effective_relation)!.label }}
            </span>
            {{ getRelationLabel(subject.effective_relation) }}
          </v-chip>
        </div>

        <v-btn icon variant="text" density="compact">
          <v-icon
            :icon="mdiChevronDown"
            :class="{ 'rotate-180': isExpanded }"
            class="transition-transform duration-200"
          />
        </v-btn>
      </template>
    </v-list-item>

    <v-expand-transition>
      <div v-if="isExpanded" class="bg-gray-50 rounded-b-lg mb-2 border-t border-gray-100">
        <div
          v-for="(permItem, i) in sortedPermissions"
          :key="i"
          class="px-4 py-3 flex items-center justify-between text-sm hover:bg-gray-100 transition-colors border-b border-gray-200 last:border-0"
          :class="{ 'bg-white': permItem.is_current_context }"
        >
          <div class="flex flex-col text-left">
            <span class="text-gray-500 text-xs font-medium mb-0.5">
              {{ translateResourceType(permItem.resource_type) }}:
            </span>
            <span class="text-primary font-medium text-[13px] leading-tight">
              {{ resolveResourceName(permItem) }}
            </span>
          </div>

          <div class="flex items-center gap-2">
            <template v-if="permItem.is_current_context">
              <v-select
                :model-value="pendingRelation ?? subject.resource_relation"
                @update:model-value="emit('change-relation', $event)"
                :disabled="!canEditAccessLevel || isMarkedForRemoval"
                :items="selectablePermissions"
                item-title="label"
                item-value="value"
                item-props
                color="primary20"
                density="compact"
                variant="outlined"
                hide-details
                class="min-w-[160px] permission-select-dense bg-white"
              />

              <v-btn
                v-if="canEditAccessLevel && subject.resource_relation"
                icon
                variant="text"
                density="comfortable"
                :color="isMarkedForRemoval ? 'primary' : 'red-lighten-1'"
                @click="emit('toggle-removal')"
                class="ml-1"
              >
                <v-icon :icon="isMarkedForRemoval ? mdiRefresh : mdiTrashCanOutline" size="small" />
              </v-btn>
            </template>

            <template v-else>
              <div
                class="bg-gray-100 px-3 py-1.5 rounded border border-gray-300 text-gray-600 text-xs font-semibold shadow-sm min-w-[160px] text-center"
              >
                <span
                  v-if="getRoleTag(permItem.resource_relation)"
                  class="text-[10px] font-semibold px-1 py-0.5 rounded mr-1 inline-block"
                  :style="{
                    border: `1px solid ${getRoleTag(permItem.resource_relation)!.borderColor}`,
                    background: getRoleTag(permItem.resource_relation)!.bgColor,
                    color: getRoleTag(permItem.resource_relation)!.textColor
                  }"
                >
                  {{ getRoleTag(permItem.resource_relation)!.label }}
                </span>
                {{ getRelationLabel(permItem.resource_relation) }}
                <span class="block text-[10px] font-normal text-gray-400 mt-0.5">(Herdado)</span>
              </div>
              <div class="w-[36px]"></div>
            </template>
          </div>
        </div>
      </div>
    </v-expand-transition>
  </div>
</template>

<script setup lang="ts">
import { mdiChevronDown, mdiRefresh, mdiTrashCanOutline } from '@mdi/js'
import { AuthorizationResourcesAndSubjects, AuthorizationRelations } from '@/types/authorization'
import type { AuthorizationRelationResponseExpanded } from '@/Network/Types/Responses/AuthorizationResponses'

interface RoleTagInfo {
  label: string
  borderColor: string
  bgColor: string
  textColor: string
}

const ROLE_TAG_MAP: Record<string, RoleTagInfo> = {
  [AuthorizationRelations.Operator]: {
    label: 'Adm',
    borderColor: '#2F7F91',
    bgColor: '#D8EEF3',
    textColor: '#2F7F91'
  },
  [AuthorizationRelations.MedicalCoordinator]: {
    label: 'Med',
    borderColor: '#198038',
    bgColor: '#EAFBEF',
    textColor: '#198038'
  },
  [AuthorizationRelations.AuditAnalyst]: {
    label: 'Adm',
    borderColor: '#2F7F91',
    bgColor: '#D8EEF3',
    textColor: '#2F7F91'
  }
}

const getRoleTag = (relation: string): RoleTagInfo | null => {
  return ROLE_TAG_MAP[relation] || null
}

export interface SortedPermItem {
  resource_type: string
  resource_id: number
  resource_relation: string
  resource_name?: string
  is_current_context: boolean
  is_inherited: boolean
}

export interface SelectablePermission {
  label: string
  value: string
  disabled?: boolean
}

defineProps<{
  subject: AuthorizationRelationResponseExpanded
  isExpanded: boolean
  isMarkedForRemoval: boolean
  canEditAccessLevel: boolean
  selectablePermissions: SelectablePermission[]
  sortedPermissions: SortedPermItem[]
  getRelationLabel: (relation: string) => string
  resolveResourceName: (permItem: SortedPermItem) => string
  pendingRelation?: string | null
}>()

const emit = defineEmits<{
  toggle: []
  'change-relation': [value: string]
  'toggle-removal': []
}>()

const getInitials = (name: string) => (name ? name.substring(0, 2).toUpperCase() : '')

const translateResourceType = (type: string) => {
  const map: Record<string, string> = {
    [AuthorizationResourcesAndSubjects.Company]: 'Empresa',
    [AuthorizationResourcesAndSubjects.HealthUnit]: 'Unidade',
    [AuthorizationResourcesAndSubjects.Schedule]: 'Escala',
    [AuthorizationResourcesAndSubjects.DsAttendanceCompany]: 'Empresa',
    [AuthorizationResourcesAndSubjects.DsAttendanceHealthUnit]: 'Unidade',
    [AuthorizationResourcesAndSubjects.DsAttendance]: 'Escala',
    [AuthorizationResourcesAndSubjects.StationExceptionalitiesCompany]: 'Empresa',
    [AuthorizationResourcesAndSubjects.StationExceptionalitiesHealthUnit]: 'Unidade',
    [AuthorizationResourcesAndSubjects.StationExceptionalitiesSchedule]: 'Escala',
    [AuthorizationResourcesAndSubjects.NpsCompany]: 'Empresa',
    [AuthorizationResourcesAndSubjects.NpsHealthUnit]: 'Unidade',
    [AuthorizationResourcesAndSubjects.PostStationCompany]: 'Empresa',
    [AuthorizationResourcesAndSubjects.PostStationHealthUnit]: 'Unidade'
  }
  return map[type] || type
}
</script>
